/* ==== model.js ==== */
/* ============================================================
   model.js — the HydroMax / HydroUltra / HydroGo switch. Loads FIRST.

   One piece of state: <html data-model="hm|hu">. One CSS rule hides
   the inactive twin ([data-m]). This file owns that attribute and
   exposes window.HX so the other scripts (app.js totals, band-a
   galleries, band-d cart / plans / reviews) never guess which
   bottle is on screen.

   Reads #hx-catalog (static, in the markup) merged with
   #hx-catalog-live (Liquid, only on Shopify) — ids, prices and
   availability follow admin; plans, ratings and copy stay static.

   Reads ?model= on load (the head bootstrap already set the
   attribute before first paint; this only confirms it). NEVER
   writes the URL: the bare page URL is the canonical, and a switch
   is not a navigation.
   ============================================================ */
(() => {
'use strict';

const root = document.documentElement;
const parse = (id) => {
  const el = document.getElementById(id);
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch (_) { return null; }
};

const cat = parse('hx-catalog');
if (!cat || !cat.models) return;

/* Live merge: the Liquid block knows the store's truth for ids, prices and
   stock. Variants are matched by id so the static finish names survive. */
const live = parse('hx-catalog-live');
if (live && live.models) {
  Object.keys(live.models).forEach((k) => {
    const L = live.models[k], S = cat.models[k];
    if (!S || !L || !L.product) return;
    S.product = L.product;
    if (L.handle) S.handle = L.handle;
    if (L.price > 0) S.price = L.price;
    if (L.compare > 0) S.compare = L.compare;
    if (typeof L.available === 'boolean') S.available = L.available;
    if (Array.isArray(L.variants) && L.variants.length) {
      S.variants = L.variants.map((v) => Object.assign(
        {}, S.variants.find((s) => String(s.id) === String(v.id)) || { finish: v.title }, v));
    }
  });
}

const KEYS = Object.keys(cat.models);
let model = KEYS.includes(root.dataset.model) ? root.dataset.model : (cat.default || KEYS[0]);
root.dataset.model = model;

const inr = (n) => '₹' + Math.round(Number(n)).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const HX = {
  get model() { return model; },
  keys: KEYS,
  inr,
  data(m) { return cat.models[m || model]; },
  unit(m) { return HX.data(m).price; },
  product(m) { return HX.data(m).product; },
  handle(m) { return HX.data(m).handle; },
  name(m) { return HX.data(m).name; },
  variants(m) { return HX.data(m).variants || []; },
  /* The variant a Buy now sells: the checked finish for models that have a
     picker, else the first available (else the first, and let the register
     say "sold out" in its own words). */
  variant(m) {
    m = m || model;
    const on = document.querySelector(`input[name="finish-${m}"]:checked`);
    if (on && on.value && !on.disabled) return Number(on.value);
    const vs = HX.variants(m);
    const v = vs.find((x) => x.available !== false) || vs[0];
    return v ? Number(v.id) : null;
  },
  variantOf(m, id) { return HX.variants(m).find((v) => String(v.id) === String(id)) || null; },
  /* The hero care-plan radios are careplan-hm / careplan-hu (distinct names so
     one card can never uncheck the other). Values are PRICES. */
  planPrice(m) {
    m = m || model;
    const on = document.querySelector(`input[name="careplan-${m}"]:checked`);
    const v = on ? Number(on.value) : 0;
    return Number.isFinite(v) && v > 0 ? v : 0;
  },
  set,
};
window.HX = HX;

/* ---------- paint: everything that is a number about a bottle ---------- */
function paint() {
  KEYS.forEach((k) => {
    const d = cat.models[k];
    document.querySelectorAll(`[data-price-of="${k}"]`).forEach((el) => { el.textContent = inr(d.price); });
    document.querySelectorAll(`[data-compare-of="${k}"]`).forEach((el) => {
      if (d.compare > d.price) { el.textContent = inr(d.compare); el.hidden = false; }
      else el.hidden = true;
    });
    document.querySelectorAll(`[data-save-of="${k}"]`).forEach((el) => {
      el.textContent = d.compare > d.price ? inr(d.compare - d.price) : '';
    });
    // Per-day framing above Buy now, and the saving in the supplements card
    // (Rs 1,500/month x 36 = Rs 54,000, the basis printed under that table).
    document.querySelectorAll(`[data-perday-of="${k}"]`).forEach((el) => { el.textContent = inr(d.price / 365); });
    document.querySelectorAll(`[data-sup-save-of="${k}"]`).forEach((el) => {
      const save = Math.floor((54000 - d.price) / 1000) * 1000;
      el.textContent = save > 0 ? inr(save) + '+' : '';
    });
    const soldOut = d.available === false ||
      (d.variants.length > 0 && d.variants.every((v) => v.available === false));
    document.querySelectorAll(`[data-avail-of="${k}"]`).forEach((el) => { el.hidden = !soldOut; });
  });
}

/* ---------- links whose WhatsApp text names the bottle ----------
   The hrefs are authored with "HydroMax" (the base page). Each one keeps its
   template on a data attribute and is rewritten with the active name. */
const NAMES = KEYS.map((k) => cat.models[k].name);
const nameRe = new RegExp(NAMES.join('|'), 'g');
const links = [...document.querySelectorAll('a[href*="wa.me"], a[href*="whatsapp.com"]')]
  .filter((a) => nameRe.test(a.getAttribute('href')));
links.forEach((a) => { a.dataset.hxHref = a.getAttribute('href').replace(nameRe, '%NAME%'); });
function relink() {
  const n = encodeURIComponent(HX.name());
  links.forEach((a) => { a.setAttribute('href', a.dataset.hxHref.replace(/%NAME%/g, n)); });
}

/* ---------- pickers: every name="model" radio follows the state ---------- */
/* Each picker has its own radio name (radios sharing a name are ONE group
   document-wide, so a shared name would let the #buy switch uncheck the hero
   card). Any radio inside [data-model-pick] is a model radio. */
function syncPickers() {
  document.querySelectorAll('[data-model-pick] input[type="radio"]').forEach((r) => {
    r.checked = r.value === model;
  });
}

/* ---------- analytics: one view_item per bottle shown ---------- */
function track() {
  if (typeof window.gtag !== 'function') return;
  const d = HX.data();
  try {
    window.gtag('event', 'view_item', {
      currency: 'INR', value: d.price,
      items: [{ item_id: String(HX.variant()), item_name: d.name, item_brand: 'Adivaa', price: d.price, quantity: 1 }]
    });
  } catch (_) { /* analytics never breaks the page */ }
}

function set(m, opts) {
  opts = opts || {};
  if (!KEYS.includes(m)) return;
  const changed = m !== model;
  model = m;
  root.dataset.model = m;
  syncPickers();
  relink();
  paint();
  if (changed || opts.force) {
    document.dispatchEvent(new CustomEvent('hx:model', { detail: { model: m, changed } }));
    /* Galleries, strips and rotators measured at 0 while hidden re-snap on
       resize. One frame later, so the display change has been laid out. */
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    track();
  }
  if (opts.scroll) {
    const hero = document.querySelector('.hero');
    const nav = document.querySelector('.nav');
    if (hero) {
      const top = hero.getBoundingClientRect().top + scrollY - (nav ? nav.getBoundingClientRect().height : 0) - 8;
      scrollTo({ top: Math.max(0, top), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }
}

document.addEventListener('change', (e) => {
  const r = e.target;
  if (r && r.type === 'radio' && r.checked && r.closest('[data-model-pick]')) set(r.value);
});
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-model-set]');
  if (!b) return;
  e.preventDefault();
  set(b.dataset.modelSet, { scroll: true });
});

/* ---------- ?variant= deep links (Shopify-standard) ----------
   Ads, feeds and the JSON-LD Offer urls use ?variant=<id>. The layout's
   pre-paint bootstrap already set data-model from the same ids, so this
   only has to pick the matching finish pill. The change event fires at
   DOMContentLoaded, when band-d's document-level listener (which retargets
   the finish-following images) is guaranteed bound. Never written back:
   the bare URL stays the one canonical. */
(() => {
  const vq = new URLSearchParams(location.search).get('variant');
  if (!vq) return;
  let m = KEYS.find((k) => (cat.models[k].variants || []).some((v) => String(v.id) === vq));
  let target = vq;
  if (!m) {
    /* Not a real bottle's id — the SHELL product's own variant ids land here.
       The layout bootstrap resolved them by title into data-hx-vhint
       (hm | hu | hug); translate to the real bottle and, for hug, its gold
       variant, so Shopify's own share links sell the right thing. */
    const hint = root.dataset.hxVhint || '';
    m = hint.slice(0, 2);
    if (!KEYS.includes(m)) return;
    const gold = hint === 'hug';
    const v = (cat.models[m].variants || []).find((x) => gold === /gold/i.test(String(x.finish || x.title || '')));
    target = v ? String(v.id) : '';
  }
  if (m !== model) set(m);
  const r = document.querySelector(`input[name="finish-${m}"][value="${target}"]`);
  if (r && !r.checked) {
    r.checked = true;
    const fire = () => r.dispatchEvent(new Event('change', { bubbles: true }));
    /* Deferred scripts execute at readyState 'interactive', BEFORE the later
       deferred files (band-d owns the finish-following images) have bound
       their listeners — and DOMContentLoaded fires only after all of them
       have run. So anything short of 'complete' waits for that event. */
    if (document.readyState !== 'complete') addEventListener('DOMContentLoaded', fire, { once: true });
    else fire();
  }
})();

/* ---------- stock: the storefront's own truth, per model ----------
   Only on the store domain (localhost has no Shopify behind it). Fails open:
   if the fetch dies the pills stay as authored and the register still has
   the final word. Disables sold-out finish pills and marks a fully sold-out
   bottle on its picker card. */
(async () => {
  if (!/adivaasmart\.com$|myshopify\.com$/.test(location.hostname)) return;
  await Promise.all(KEYS.map(async (k) => {
    const d = cat.models[k];
    if (!d.handle) return;
    try {
      const r = await fetch(`/products/${d.handle}.js`, { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const p = await r.json();
      const avail = new Map((p.variants || []).map((v) => [String(v.id), v.available]));
      d.variants.forEach((v) => { if (avail.has(String(v.id))) v.available = avail.get(String(v.id)); });
      d.available = p.available;
      document.querySelectorAll(`input[name="finish-${k}"]`).forEach((r) => {
        if (avail.get(r.value) === false) {
          r.disabled = true;
          const small = r.nextElementSibling && r.nextElementSibling.querySelector('small');
          if (small) small.textContent = 'Sold out';
        }
      });
      const on = document.querySelector(`input[name="finish-${k}"]:checked`);
      if (on && on.disabled) {
        const other = document.querySelector(`input[name="finish-${k}"]:not(:disabled)`);
        if (other) { other.checked = true; other.dispatchEvent(new Event('change', { bubbles: true })); }
      }
    } catch (_) { /* fail open */ }
  }));
  paint();
  document.dispatchEvent(new CustomEvent('hx:stock'));
})();

/* first paint */
syncPickers();
relink();
paint();
addEventListener('load', track, { once: true });

})();

/* ==== app.js ==== */
/* ============================================================
   Adivaa combined page (HydroMax + HydroUltra) — CURRENT
   Two motion moves only: Tick and Bubble. Rise (scroll fade-in-up) and
   Waterline (the blue sweep over the hero image) were removed page-wide.
   ============================================================ */
(() => {
'use strict';
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const fmt = new Intl.NumberFormat('en-IN');

/* ---------- Tick ---------- */
function tick(el, target, dur = 1400) {
  if (reduced) { el.textContent = fmt.format(target); return; }
  const from = parseInt(String(el.textContent).replace(/[^\d-]/g, ''), 10) || 0;
  const t0 = performance.now();
  const step = (t) => {
    const p = Math.min((t - t0) / dur, 1);
    const e = 1 - Math.pow(1 - p, 4);
    el.textContent = fmt.format(Math.round(from + (target - from) * e));
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
const cio = new IntersectionObserver((es) => {
  es.forEach(e => { if (e.isIntersecting) { tick(e.target, +e.target.dataset.count); cio.unobserve(e.target); } });
}, { threshold: 0.6 });
document.querySelectorAll('[data-count]').forEach(el => cio.observe(el));

/* ---------- meters ---------- */
const mio = new IntersectionObserver((es) => {
  es.forEach(e => {
    if (!e.isIntersecting) return;
    e.target.querySelector('i').style.width = e.target.dataset.meter + '%';
    mio.unobserve(e.target);
  });
}, { threshold: 0.4 });
document.querySelectorAll('[data-meter]').forEach(el => mio.observe(el));

/* ---------- Bubble ---------- */
if (reduced) {
  document.querySelectorAll('.bubbles').forEach(b => b.remove());
} else {
  document.querySelectorAll('[data-bubbles]').forEach(box => {
    const n = +box.dataset.bubbles;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < n; i++) {
      const b = document.createElement('i');
      const size = 4 + Math.random() * 16;
      b.style.width = b.style.height = size + 'px';
      b.style.left = (Math.random() * 100) + '%';
      b.style.animationDuration = (12 + Math.random() * 10) + 's';
      b.style.animationDelay = (-Math.random() * 22) + 's';
      b.style.setProperty('--dx', (Math.random() * 60 - 30) + 'px');
      frag.appendChild(b);
    }
    box.appendChild(frag);
  });
}

/* ---------- marquee: clone the track for a seamless loop ----------
   The logo strip is ~49 KB of inline SVG. Shipping it twice in the HTML to make
   the 50%-translate loop work would double that for no benefit, so the markup
   carries one copy and the duplicate is made here. */
document.querySelectorAll('[data-marquee]').forEach(track => {
  track.insertAdjacentHTML('beforeend', track.innerHTML);
  track.setAttribute('aria-hidden', 'true');
});

/* ---------- buy block: quantity × care plan → live total ---------- */
(() => {
  /* The unit price follows the model on screen (model.js). 16999 is the
     JS-off / catalog-missing fallback only. */
  const MAX_QTY = 9;
  const unit = () => (window.HX ? HX.unit() : 16999);
  const box    = document.querySelector('[data-qty]');
  const out    = document.querySelector('[data-qty-value]');
  const totals = document.querySelectorAll('[data-total]');
  /* The prepaid line under the buy button. Optional on purpose — the page is
     still whole without it, so a copy edit that drops it must not take the
     quantity stepper down with it. */
  const prepaid = document.querySelectorAll('[data-prepaid]');
  const PREPAID_OFF = 0.05;
  if (!box || !out || !totals.length) return;

  const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
  let qty = 1;

  /* careplan-hm / careplan-hu: distinct radio groups per bottle, read through
     model.js so the total always prices the plan of the bottle on screen. */
  function planPrice() {
    return window.HX ? HX.planPrice() : 0;
  }
  function render() {
    out.textContent = qty;
    // The plan is charged ONCE per order, not per bottle: Protega's minted
    // plan products are quantity-capped at 1 (measured 2026-08-16 — a fresh
    // mint refuses quantity 2 with a 422 on both stores' plans), so a
    // (unit + plan) x qty promise could never ring up at qty 2+. The button
    // promises exactly what the register can charge.
    const total = unit() * qty + planPrice();
    totals.forEach(t => { t.textContent = inr.format(total); });
    /* The prepaid price follows the same total, discount included, because the
       checkout's offer is an order-level 5% — it does not stop at the bottle.
       Rounded to the rupee (the totals carry no paise either), and rounded
       DOWN so the line can only ever understate what the customer saves. */
    prepaid.forEach(p => { p.textContent = inr.format(Math.floor(total * (1 - PREPAID_OFF))); });
    box.querySelector('[data-step="-1"]').disabled = qty <= 1;
    box.querySelector('[data-step="1"]').disabled  = qty >= MAX_QTY;
  }
  box.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-step]'); if (!btn) return;
    qty = Math.min(MAX_QTY, Math.max(1, qty + (+btn.dataset.step)));
    render();
  });
  document.querySelectorAll('input[name^="careplan"]').forEach(r => r.addEventListener('change', render));
  /* A model switch changes the unit price and which plan group is live. */
  document.addEventListener('hx:model', render);
  render();
})();

/* ---------- PPB comparison bars (session 1) ---------- */
const bio = new IntersectionObserver((es) => {
  es.forEach(e => {
    if (!e.isIntersecting) return;
    e.target.style.width = e.target.dataset.bar + '%';
    bio.unobserve(e.target);
  });
}, { threshold: 0.5 });
document.querySelectorAll('[data-bar]').forEach(el => bio.observe(el));

/* ---------- safety net ----------
   IntersectionObserver does not fire while the document is hidden (background
   tab, occluded pane). Without this, such a load leaves the page blank until
   the user scrolls. */
function sweepVisible() {
  const vh = innerHeight;
  document.querySelectorAll('[data-count]').forEach(el => {
    const r = el.getBoundingClientRect();
    if (el.textContent === '0' && r.top < vh && r.bottom > 0) { tick(el, +el.dataset.count); cio.unobserve(el); }
  });
  document.querySelectorAll('[data-meter]').forEach(el => {
    const r = el.getBoundingClientRect();
    if (r.top < vh && r.bottom > 0) el.querySelector('i').style.width = el.dataset.meter + '%';
  });
  document.querySelectorAll('[data-bar]').forEach(el => {
    const r = el.getBoundingClientRect();
    if (r.top < vh && r.bottom > 0) el.style.width = el.dataset.bar + '%';
  });
}
addEventListener('load', () => setTimeout(sweepVisible, 1200));

/* ---------- progress + active nav ---------- */
const bar = document.getElementById('progress');
/* The header now mirrors the live store, so #navlinks holds site links
   ("/", "/pages/...") rather than in-page anchors. Only "#..." hrefs are
   valid selectors — passing "/" to querySelector throws a SyntaxError and
   would take the rest of this file down with it. Filter first.
   The active-link highlight simply no-ops while no anchor links are present;
   the progress bar is independent of the nav and keeps working. */
const links = [...document.querySelectorAll('#navlinks a')].filter(a => a.getAttribute('href').startsWith('#'));
const targets = links.map(a => document.querySelector(a.getAttribute('href'))).filter(Boolean);
let ticking = false;
function onScroll() {
  const max = document.documentElement.scrollHeight - innerHeight;
  bar.style.width = (max > 0 ? (scrollY / max) * 100 : 0) + '%';
  let active = -1;
  targets.forEach((t, i) => { if (t.getBoundingClientRect().top <= 150) active = i; });
  links.forEach((a, i) => a.classList.toggle('is-active', i === active));
  sweepVisible();
  ticking = false;
}
addEventListener('scroll', () => {
  if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
}, { passive: true });
onScroll();

/* requestAnimationFrame does not run in a hidden document, so a scroll that happens
   while the tab is backgrounded leaves `ticking` latched and the progress bar and
   active nav link stale. Clear the latch and recompute on the way back in.
   Registered here, after `ticking` and `onScroll` exist. */
addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  ticking = false;
  sweepVisible();
  onScroll();
});

/* ---------- mobile menu drawer ----------
   Below 1080px the desktop nav is hidden, so this is the only route to the
   store's pages. Closing is deliberately over-served — scrim, close button,
   Esc, following a link, and crossing back to desktop width — because a
   drawer you cannot dismiss traps the page. */
const drawer = document.getElementById('drawer');
const burger = document.getElementById('burger');
if (drawer && burger) {
  const desktop = matchMedia('(min-width: 1081px)');

  function openDrawer() {
    drawer.classList.add('open');
    burger.setAttribute('aria-expanded', 'true');
    burger.setAttribute('aria-label', 'Close menu');
    /* The lock itself is CSS, gated by the drawer's own breakpoint — see
       styles.css. All this does is set the flag and hand over the scrollbar
       width so the page does not jump as the scrollbar goes. */
    const gap = innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--sbw', gap > 0 ? gap + 'px' : '0px');
    document.documentElement.classList.add('drawer-open');
    drawer.querySelector('.drawer__links a').focus();
  }

  function closeDrawer({ restoreFocus = true } = {}) {
    if (!drawer.classList.contains('open')) return;
    drawer.classList.remove('open');
    burger.setAttribute('aria-expanded', 'false');
    burger.setAttribute('aria-label', 'Open menu');
    document.documentElement.classList.remove('drawer-open');
    /* Focus goes back to the button that opened it, not to whatever happened
       to be focused beforehand — Safari does not focus a button on click, so
       remembering the previous element strands focus on <body> there. */
    if (restoreFocus) burger.focus();
  }

  burger.addEventListener('click', () => {
    drawer.classList.contains('open') ? closeDrawer() : openDrawer();
  });
  drawer.addEventListener('click', (e) => {
    if (e.target.closest('[data-drawer-close]')) closeDrawer();
    /* a link inside the drawer navigates; leave focus alone on the way out */
    else if (e.target.closest('a')) closeDrawer({ restoreFocus: false });
  });
  addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });
  /* resizing up to desktop hides the drawer in CSS; without this the body
     would stay scroll-locked with nothing on screen explaining why */
  desktop.addEventListener('change', (e) => { if (e.matches) closeDrawer({ restoreFocus: false }); });
}

/* ---------- sticky buy bar ---------- */
const buybar = document.getElementById('buybar');
const hero = document.querySelector('.hero');
if (buybar && hero) {
  new IntersectionObserver(([e]) => buybar.classList.toggle('show', !e.isIntersecting), { threshold: 0 })
    .observe(hero);
}
})();

/* #specs: on desktop the tall photo left a hole under the one open drawer
   (user call 2026-09-18), so Build & hardware opens too at 981px+. Phones
   stack the section and keep just Performance open. */
if (window.matchMedia('(min-width: 981px)').matches) {
  document.querySelectorAll('details[data-open-wide]').forEach((d) => { d.open = true; });
}
